import type { GameBoard, GameConfig, GamePosition, GameResult } from '../types'

import { generateBoardStrict } from './generateBoard'

/**
 * Service responsável pela lógica de negócio do WordHunt
 * Usa apenas o gerador determinístico (forward only) por backtracking.
 */
export class WordHuntService {
  private static readonly BOARD_SIZE = 10
  private static readonly MIN_WORD_LENGTH = 3

  // Direções "forward": →, ↓, ↘, ↙ (para validação)
  private static readonly FORWARD_DELTAS: readonly Readonly<
    [number, number]
  >[] = [
    [0, 1], // →
    [1, 0], // ↓
    [1, 1], // ↘
    [1, -1] // ↙
  ] as const

  /**
   * Normaliza a lista de palavras (maiusc, remove curtas, remove reversos duplicados)
   */
  private static normalizeWord(word: string): string {
    return word
      .toUpperCase()
      .normalize('NFKD')
      .replace(/[^\p{Letter}\p{Number}]/gu, '')
  }

  static createGameConfig(words: string[], showWords = false): GameConfig {
    const processedWords = words
      .map(w => this.normalizeWord(w))
      .filter(w => w.length >= this.MIN_WORD_LENGTH)
      .filter((w, idx, arr) => {
        const rev = w.split('').reverse().join('')
        return !arr.slice(0, idx).includes(rev)
      })

    return {
      boardSize: this.BOARD_SIZE,
      minWordLength: this.MIN_WORD_LENGTH,
      words: processedWords,
      showWords
    }
  }

  /**
   * Gera o tabuleiro usando apenas o generateBoardStrict (sem overwrite).
   * 1ª tentativa: só HV (sem diagonais). Se não couber, tenta com diagonais.
   */
  static generateBoard(config: GameConfig): GameBoard {
    // Tentativa conservadora: sem diagonais
    try {
      const boardHV = generateBoardStrict({
        boardSize: config.boardSize,
        words: config.words,
        allowDiagLeft: false,
        allowDiagRight: false,
        seed: Date.now() & 0xffff // seed simples p/ variedade reprodutível no processo
      })
      // valida que TODAS as palavras estão forward no board
      if (this.validateGeneratedBoard(boardHV, config.words)) return boardHV
      throw new Error('Board HV gerado, mas falhou na validação.')
    } catch (_e) {
      // Recomeça com diagonais habilitadas
    }

    const boardDiag = generateBoardStrict({
      boardSize: config.boardSize,
      words: config.words,
      allowDiagLeft: true,
      allowDiagRight: true,
      seed: (Date.now() * 2654435761) & 0xffff
    })

    if (!this.validateGeneratedBoard(boardDiag, config.words)) {
      throw new Error(
        'Falha de validação: nem com diagonais todas as palavras foram encontradas.'
      )
    }

    return boardDiag
  }

  /**
   * Valida se todas as palavras estão presentes no board (direções forward)
   */
  private static validateGeneratedBoard(
    board: GameBoard,
    expectedWords: string[]
  ): boolean {
    for (const word of expectedWords) {
      if (!this.findWordInBoard(board, word, this.FORWARD_DELTAS)) {
        console.warn(`Palavra "${word}" não encontrada no board gerado`)
        return false
      }
    }
    return true
  }

  private static findWordInBoard(
    board: GameBoard,
    word: string,
    deltas: readonly Readonly<[number, number]>[] = this.FORWARD_DELTAS
  ): boolean {
    for (let row = 0; row < board.rows; row++) {
      for (let col = 0; col < board.columns; col++) {
        if (board.board[row][col].letter === word[0]) {
          for (const [dr, dc] of deltas) {
            if (this.checkWordDirection(board, word, row, col, dr, dc)) {
              return true
            }
          }
        }
      }
    }
    return false
  }

  private static checkWordDirection(
    board: GameBoard,
    word: string,
    startRow: number,
    startCol: number,
    deltaRow: number,
    deltaCol: number
  ): boolean {
    for (let i = 0; i < word.length; i++) {
      const r = startRow + i * deltaRow
      const c = startCol + i * deltaCol
      if (r < 0 || r >= board.rows || c < 0 || c >= board.columns) return false
      if (board.board[r][c].letter !== word[i]) return false
    }
    return true
  }

  /**
   * Validação de seleção do usuário
   */
  static validateWord(
    board: GameBoard,
    positions: GamePosition[],
    foundWords: string[]
  ): { isValid: boolean; word?: string; reason?: string } {
    if (positions.length < this.MIN_WORD_LENGTH) {
      return { isValid: false, reason: 'Palavra muito pequena' }
    }

    const word = this.buildWordFromPositions(board, positions)

    if (foundWords.includes(word)) {
      return { isValid: false, reason: 'Já encontrou essa palavra' }
    }

    if (!board.words.includes(word)) {
      return { isValid: false, reason: 'Não é uma palavra desse jogo' }
    }

    return { isValid: true, word }
  }

  private static buildWordFromPositions(
    board: GameBoard,
    positions: GamePosition[]
  ): string {
    return positions
      .map(pos => board.board[pos.row][pos.column].letter)
      .join('')
  }

  static calculateScore(foundWords: string[]): number {
    const wordScores = [1, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]
    return foundWords.reduce((total, word) => {
      const index = Math.max(0, word.length - this.MIN_WORD_LENGTH)
      const scoreIndex = Math.min(index, wordScores.length - 1)
      return total + wordScores[scoreIndex]
    }, 0)
  }

  static isGameComplete(board: GameBoard, foundWords: string[]): boolean {
    return board.words.length === foundWords.length
  }

  static calculateGameResult(
    board: GameBoard,
    foundWords: string[]
  ): GameResult {
    const score = this.calculateScore(foundWords)
    const totalWords = board.words.length
    const completionPercentage = (foundWords.length / totalWords) * 100
    return { score, foundWords, totalWords, completionPercentage }
  }

  static getFoundPositions(
    _board: GameBoard,
    _foundWords: string[]
  ): GamePosition[] {
    return []
  }

  static isAdjacentPosition(
    currentPos: GamePosition,
    lastPos: GamePosition | null
  ): boolean {
    if (!lastPos) return true
    const rowDiff = Math.abs(currentPos.row - lastPos.row)
    const colDiff = Math.abs(currentPos.column - lastPos.column)
    return rowDiff <= 1 && colDiff <= 1 && !(rowDiff === 0 && colDiff === 0)
  }

  static removeLastPosition(positions: GamePosition[]): GamePosition[] {
    return positions.slice(0, -1)
  }
}
